import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { isWeb } from "@memorang/helpers";
import { setOpacity } from "@memorang/helpers/src/common";
import { useRef, useState } from "react";
import { Pressable, TouchableOpacity, View } from "react-native";
import { Menu, Portal, Text } from "react-native-paper";
import { themeRoundnessMultiplier } from "../../constants";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import MarkdownRenderer from "../MarkdownRenderer";

export type Props = {
	text: string;
	type: "voice" | "text";
	duration?: string;
	onCopy?: () => void;
};

const Message = ({ text, type, duration, onCopy }: Props) => {
	const theme = useAppTheme();
	const [isHovering, setIsHovering] = useState(false);
	const [menuVisible, setMenuVisible] = useState(false);
	const [isCopied, setIsCopied] = useState(false);
	const [anchorPos, setAnchorPos] = useState({ x: 0, y: 0 });
	const durationRef = useRef<View | null>(null);

	const showMenu = () => {
		if (isWeb) {
			return;
		}
		durationRef.current?.measure((_, __, width, height, pageX, pageY) => {
			setAnchorPos({
				x: pageX + width,
				y: pageY + height,
			});
			setMenuVisible(true);
		});
	};

	const handleCopy = () => {
		onCopy?.();
		setIsCopied(true);
		setTimeout(() => {
			setIsCopied(false);
		}, 1500); // Show checkmark for 1.5 seconds
	};

	return (
		<Box
			onMouseEnter={() => setIsHovering(true)}
			onMouseLeave={() => setIsHovering(false)}
			flexDirection="column"
			gap={type === "voice" ? 8 : 4}
			paddingTop={8}
			paddingBottom={isWeb ? 0 : 8}
		>
			<Pressable
				onLongPress={showMenu}
				style={{ cursor: "auto" }}
				delayLongPress={300}
			>
				<Box
					borderRadius={theme.roundness * 5}
					style={{
						borderBottomRightRadius: theme.roundness * themeRoundnessMultiplier,
					}}
					backgroundColor={theme.colors.secondary}
					paddingHorizontal={16}
					paddingVertical={8}
					alignSelf="flex-end"
				>
					<MarkdownRenderer
						text={text}
						customStyles={{
							text: {
								color: theme.colors.onSecondary,
								fontStyle: type === "voice" ? "italic" : "normal",
							},
						}}
					/>
				</Box>
			</Pressable>
			<View
				style={{
					height: type === "voice" ? 24 : 1,
					flexDirection: "row",
					alignItems: "center",
					alignSelf: "flex-end",
					justifyContent: "flex-end",
					width: 45,
				}}
				ref={durationRef}
			>
				{type === "voice" && (
					<Box flexDirection="row" justifyContent="flex-end">
						<Icon name="microphone" size={16} color={theme.colors.secondary} />
						<Text style={{ color: theme.colors.secondary }} variant="bodySmall">
							{duration}
						</Text>
					</Box>
				)}
			</View>

			<Portal>
				{menuVisible && !isWeb && (
					<>
						{/* Semi-transparent overlay background */}
						<Pressable
							onPress={() => setMenuVisible(false)}
							style={{
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								backgroundColor: setOpacity(theme.colors.shadow, 0.5),
								zIndex: 1000,
							}}
						/>

						{/* Menu positioned at calculated coordinates */}
						<Box
							position="absolute"
							left={anchorPos.x - 150} // Center the menu on the anchor point
							top={anchorPos.y + 0} // Add some spacing below the anchor
							zIndex={10000}
							borderRadius={theme.roundness * themeRoundnessMultiplier}
							minWidth={150}
							backgroundColor={theme.colors.surfaceContainer}
							style={{
								paddingVertical: 4,
								shadowColor: "black",
								shadowOffset: { width: 0, height: 2 },
								shadowOpacity: 0.25,
								shadowRadius: 3.84,
								elevation: 5,
							}}
						>
							<Menu
								visible={menuVisible && !isWeb}
								onDismiss={() => setMenuVisible(false)}
								anchor={<Box width={1} height={1} />}
							>
								<Menu.Item
									leadingIcon="content-copy"
									title="Copy"
									dense={true}
									onPress={() => {
										handleCopy();
										setMenuVisible(false);
									}}
								/>
							</Menu>
						</Box>
					</>
				)}
			</Portal>
			{isWeb &&
				(isHovering && !menuVisible ? (
					<TouchableOpacity
						onPress={handleCopy}
						style={{
							alignSelf: "flex-end",
							height: 32,
							width: 32,
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Box>
							<Icon
								name={isCopied ? "check" : "content-copy"}
								size={16}
								color={theme.colors.primary}
							/>
						</Box>
					</TouchableOpacity>
				) : (
					<Box style={{ alignSelf: "flex-end", height: 32, width: 32 }} />
				))}
		</Box>
	);
};

export default Message;
